import type { DemoAction } from './actions.ts';
import { buildAuthenticatedHeaders } from './authStorage.ts';
import { scenarioFocusOrderId, scenarioFocusQuoteId } from './model.ts';
import type { DemoSnapshot } from './types.ts';

export class HttpDemoApiError extends Error {
  readonly kind: 'network' | 'http';
  readonly statusCode: number | null;

  constructor(message: string, kind: 'network' | 'http', statusCode: number | null = null) {
    super(message);
    this.name = 'HttpDemoApiError';
    this.kind = kind;
    this.statusCode = statusCode;
  }
}

function getDemoApiBaseUrl(): string {
  const raw = String(import.meta.env.VITE_DEMO_API_BASE_URL || '/api').trim();
  if (!raw) return '/api';
  return raw.endsWith('/') ? raw.slice(0, -1) : raw;
}

async function requestJson<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;

  try {
    response = await fetch(`${getDemoApiBaseUrl()}${path}`, {
      ...init,
      credentials: 'include',
      headers: buildAuthenticatedHeaders({
        Accept: 'application/json',
        ...(init?.headers || {}),
      }),
    });
  } catch (error) {
    const detail = error instanceof Error ? error.message : 'Erreur réseau';
    throw new HttpDemoApiError(detail, 'network');
  }

  const contentType = response.headers.get('content-type') || '';
  if (!response.ok) {
    let detail = response.statusText;
    if (contentType.includes('application/json')) {
      const payload = (await response.json()) as { detail?: string; error?: string };
      detail = payload.detail || payload.error || detail;
    } else {
      detail = (await response.text()) || detail;
    }
    throw new HttpDemoApiError(`HTTP ${response.status}: ${detail}`, 'http', response.status);
  }
  if (!contentType.includes('application/json')) {
    throw new HttpDemoApiError(
      `Type de contenu inattendu renvoyé par l’API Service Terrain : ${contentType || 'inconnu'}`,
      'http',
      response.status,
    );
  }

  return (await response.json()) as T;
}

export async function fetchHttpDemoSnapshot(signal?: AbortSignal): Promise<DemoSnapshot> {
  return requestJson<DemoSnapshot>('/demo/snapshot', { signal });
}

export async function addProbeCheckHttp(quoteId: string): Promise<unknown> {
  return requestJson<unknown>(`/mvp/quotes/${encodeURIComponent(quoteId)}/probe-check`, { method: 'POST' });
}

export async function convertQuoteHttp(quoteId: string): Promise<unknown> {
  return requestJson<unknown>(`/mvp/quotes/${encodeURIComponent(quoteId)}/convert`, { method: 'POST' });
}

export async function dispatchWorkOrderHttp(
  workOrderId: string,
  payload: { ownerName: string; scheduledWindow: string },
): Promise<unknown> {
  return requestJson<unknown>(`/mvp/work-orders/${encodeURIComponent(workOrderId)}/dispatch`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
}

export async function startWorkOrderHttp(workOrderId: string): Promise<unknown> {
  return requestJson<unknown>(`/mvp/work-orders/${encodeURIComponent(workOrderId)}/start`, { method: 'POST' });
}

export async function captureWorkOrderPhotosHttp(workOrderId: string): Promise<unknown> {
  return requestJson<unknown>(`/mvp/work-orders/${encodeURIComponent(workOrderId)}/photos`, { method: 'POST' });
}

export async function finalizeWorkOrderVoiceHttp(workOrderId: string): Promise<unknown> {
  return requestJson<unknown>(`/mvp/work-orders/${encodeURIComponent(workOrderId)}/voice/finalize`, { method: 'POST' });
}

export async function captureWorkOrderSignatureHttp(workOrderId: string): Promise<unknown> {
  return requestJson<unknown>(`/mvp/work-orders/${encodeURIComponent(workOrderId)}/signature`, { method: 'POST' });
}

export async function markWorkOrderSyncConflictHttp(workOrderId: string): Promise<unknown> {
  return requestJson<unknown>(`/mvp/work-orders/${encodeURIComponent(workOrderId)}/sync/conflict`, { method: 'POST' });
}

export async function retryWorkOrderSyncHttp(workOrderId: string): Promise<unknown> {
  return requestJson<unknown>(`/mvp/work-orders/${encodeURIComponent(workOrderId)}/sync/retry`, { method: 'POST' });
}

export async function completeWorkOrderSyncRetryHttp(workOrderId: string): Promise<unknown> {
  return requestJson<unknown>(`/mvp/work-orders/${encodeURIComponent(workOrderId)}/sync/complete`, { method: 'POST' });
}

export async function submitWorkOrderReportHttp(workOrderId: string): Promise<unknown> {
  return requestJson<unknown>(`/mvp/work-orders/${encodeURIComponent(workOrderId)}/report/submit`, { method: 'POST' });
}

export async function validateWorkOrderReportHttp(workOrderId: string): Promise<unknown> {
  return requestJson<unknown>(`/mvp/work-orders/${encodeURIComponent(workOrderId)}/report/validate`, { method: 'POST' });
}

export async function rejectWorkOrderReportHttp(
  workOrderId: string,
  payload: { rejectionReason: string },
): Promise<unknown> {
  return requestJson<unknown>(`/mvp/work-orders/${encodeURIComponent(workOrderId)}/report/reject`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
}

export async function createWorkOrderInvoiceHttp(workOrderId: string): Promise<unknown> {
  return requestJson<unknown>(`/mvp/work-orders/${encodeURIComponent(workOrderId)}/invoice`, { method: 'POST' });
}

export async function proposeFollowUpQuoteHttp<T>(workOrderId: string): Promise<T> {
  return requestJson<T>(`/mvp/work-orders/${encodeURIComponent(workOrderId)}/follow-up`, { method: 'POST' });
}

function buildWriteRequest(action: DemoAction): { path: string; init: RequestInit } {
  const focusQuoteId = encodeURIComponent(scenarioFocusQuoteId);
  const focusWorkOrderId = encodeURIComponent(scenarioFocusOrderId);

  switch (action.type) {
    case 'demo:reset':
      return {
        path: '/mvp/reset',
        init: { method: 'POST' },
      };
    case 'quote:add-probe-check':
      return {
        path: `/mvp/quotes/${focusQuoteId}/probe-check`,
        init: { method: 'POST' },
      };
    case 'quote:convert':
      return {
        path: `/mvp/quotes/${focusQuoteId}/convert`,
        init: { method: 'POST' },
      };
    case 'dispatch:assign':
      return {
        path: `/mvp/work-orders/${focusWorkOrderId}/dispatch`,
        init: {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ownerName: action.ownerName,
            scheduledWindow: action.scheduledWindow,
          }),
        },
      };
    case 'job:start':
      return {
        path: `/mvp/work-orders/${focusWorkOrderId}/start`,
        init: { method: 'POST' },
      };
    case 'job:capture-photos':
      return {
        path: `/mvp/work-orders/${focusWorkOrderId}/photos`,
        init: { method: 'POST' },
      };
    case 'job:finalize-voice':
      return {
        path: `/mvp/work-orders/${focusWorkOrderId}/voice/finalize`,
        init: { method: 'POST' },
      };
    case 'job:capture-signature':
      return {
        path: `/mvp/work-orders/${focusWorkOrderId}/signature`,
        init: { method: 'POST' },
      };
    case 'sync:mark-conflict':
      return {
        path: `/mvp/work-orders/${focusWorkOrderId}/sync/conflict`,
        init: { method: 'POST' },
      };
    case 'sync:set-retrying':
      return {
        path: `/mvp/work-orders/${focusWorkOrderId}/sync/retry`,
        init: { method: 'POST' },
      };
    case 'sync:complete-retry':
      return {
        path: `/mvp/work-orders/${focusWorkOrderId}/sync/complete`,
        init: { method: 'POST' },
      };
    case 'report:submit':
      return {
        path: `/mvp/work-orders/${focusWorkOrderId}/report/submit`,
        init: { method: 'POST' },
      };
    case 'report:validate':
      return {
        path: `/mvp/work-orders/${focusWorkOrderId}/report/validate`,
        init: { method: 'POST' },
      };
    case 'report:reject':
      return {
        path: `/mvp/work-orders/${focusWorkOrderId}/report/reject`,
        init: {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ rejectionReason: action.rejectionReason }),
        },
      };
    case 'invoice:create':
      return {
        path: `/mvp/work-orders/${focusWorkOrderId}/invoice`,
        init: { method: 'POST' },
      };
  }

  throw new Error('Unsupported HTTP action.');
}

export async function runHttpDemoAction(action: DemoAction): Promise<DemoSnapshot> {
  const request = buildWriteRequest(action);
  return requestJson<DemoSnapshot>(request.path, request.init);
}
